import { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { businessApi, eventApi } from '../services/api';
import { Business, Event } from '../types';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

export function CheckIn() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedBiz, setSelectedBiz] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [bookings, setBookings] = useState<any[]>([]);
  const [stats, setStats] = useState({ total: 0, checkedIn: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [checkinResult, setCheckinResult] = useState<any>(null);
  const [checkinModal, setCheckinModal] = useState<any>(null);
  const [personasLlegaron, setPersonasLlegaron] = useState(1);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (selectedEvent) loadCheckins(); }, [selectedEvent]);

  const token = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

  const fetchData = async () => {
    try {
      const bRes = await businessApi.getMyBusinesses();
      const list = bRes.data.data.businesses || [];
      setBusinesses(list);
      if (list.length > 0) {
        setSelectedBiz(list[0].id);
        const eRes = await eventApi.getBusinessEvents(list[0].id);
        setEvents(eRes.data.data.events || []);
      }
    } catch { toast.error('Error cargando datos'); }
    finally { setLoading(false); }
  };

  const handleBizChange = async (bizId: string) => {
    setSelectedBiz(bizId);
    setSelectedEvent('');
    setBookings([]);
    try {
      const eRes = await eventApi.getBusinessEvents(bizId);
      setEvents(eRes.data.data.events || []);
    } catch {}
  };

  const loadCheckins = async () => {
    try {
      const r = await axios.get(`${API_URL}/checkin/event/${selectedEvent}`, { headers: token() });
      const data = r.data.data;
      setBookings(data.bookings || []);
      setStats(data.stats || { total: 0, checkedIn: 0, pending: 0 });
    } catch { toast.error('Error cargando inscritos'); }
  };

  const doCheckin = (bookingId: string, client: any, alreadyDone: boolean, numPersonas: number) => {
    if (alreadyDone) {
      setCheckinResult({ already: true, client });
      return;
    }
    setPersonasLlegaron(numPersonas);
    setCheckinModal({ bookingId, client, numPersonas });
  };

  const confirmarCheckin = async (personasReales: number) => {
    if (!checkinModal) return;
    try {
      await axios.post(`${API_URL}/checkin/manual/${checkinModal.bookingId}`,
        { personasLlegaron: personasReales },
        { headers: token() }
      );
      setCheckinModal(null);
      setCheckinResult({ success: true, client: checkinModal.client, personasLlegaron: personasReales, totalReservadas: checkinModal.numPersonas });
      loadCheckins();
    } catch (e: any) {
      const msg = e?.response?.data?.message || '';
      if (msg.includes('ya') || msg.includes('already')) {
        setCheckinModal(null);
        setCheckinResult({ already: true, client: checkinModal.client });
      } else {
        toast.error('Error al hacer check-in');
      }
    }
  };

  const filtered = bookings.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${b.client?.firstName} ${b.client?.lastName} ${b.client?.email}`.toLowerCase().includes(q);
  });

  const checkedInPct = stats.total > 0 ? Math.round((stats.checkedIn / stats.total) * 100) : 0;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-4">
      <style>{`
        .ci-row { display:flex; align-items:center; gap:12px; padding:14px 16px; background:white; border:1px solid #e2e8f0; border-radius:12px; margin-bottom:8px; transition:all .2s; }
        .ci-row.done { background:#f0fdf4; border-color:#86efac; }
        .ci-avatar { width:42px; height:42px; border-radius:50%; background:linear-gradient(135deg,#3b82f6,#8b5cf6); display:flex; align-items:center; justify-content:center; color:white; font-weight:700; font-size:.88rem; flex-shrink:0; }
        .ci-avatar.done { background:linear-gradient(135deg,#10b981,#059669); }
        .ci-btn { padding:9px 18px; border-radius:9px; border:none; font-weight:700; font-size:.82rem; cursor:pointer; font-family:inherit; transition:all .2s; }
        .modal-bg { position:fixed; inset:0; background:rgba(0,0,0,.6); z-index:200; display:flex; align-items:center; justify-content:center; padding:20px; backdrop-filter:blur(8px); }
        .modal-box { background:white; border-radius:24px; padding:36px; max-width:360px; width:100%; text-align:center; box-shadow:0 40px 100px rgba(0,0,0,.25); animation:fadeUp .2s ease; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* HEADER */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">✅ Check-in</h1>
        <p className="text-secondary-500 text-sm mt-1">Registra la asistencia de tus clientes</p>
      </div>

      {/* SELECCIÓN */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {businesses.length > 1 && (
            <div>
              <label className="label">Negocio</label>
              <select className="input" value={selectedBiz} onChange={e => handleBizChange(e.target.value)}>
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="label">Evento</label>
            <select className="input" value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)}>
              <option value="">— Seleccionar evento —</option>
              {events.map(e => (
                <option key={e.id} value={e.id}>
                  {e.title} · {new Date(e.startTime).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'Europe/Madrid' })}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {selectedEvent && (
        <>
          {/* STATS */}
          <div className="grid grid-cols-3 gap-3">
            <div className="card p-4 text-center">
              <p className="text-2xl font-bold text-secondary-900">{stats.total}</p>
              <p className="text-xs text-secondary-400 mt-1">👥 Total personas</p>
            </div>
            <div className="card p-4 text-center" style={{borderColor:'#86efac',background:'#f0fdf4'}}>
              <p className="text-2xl font-bold text-green-600">{stats.checkedIn}</p>
              <p className="text-xs text-secondary-400 mt-1">✅ Han llegado</p>
            </div>
            <div className="card p-4 text-center" style={{borderColor:'#fcd34d',background:'#fef9c3'}}>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-xs text-secondary-400 mt-1">⏳ Pendientes</p>
            </div>
          </div>

          {/* BARRA */}
          <div className="card p-4">
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
              <span style={{fontSize:'.82rem',fontWeight:600,color:'#475569'}}>Asistencia</span>
              <span style={{fontSize:'.82rem',fontWeight:800,color:checkedInPct>=80?'#10b981':'#f59e0b'}}>{checkedInPct}%</span>
            </div>
            <div style={{background:'#f1f5f9',borderRadius:100,height:8}}>
              <div style={{height:8,borderRadius:100,width:`${checkedInPct}%`,background:checkedInPct>=80?'#10b981':'#3b82f6',transition:'width .5s'}}></div>
            </div>
          </div>

          {/* BÚSQUEDA */}
          <input type="text" className="input" placeholder="🔍 Buscar cliente por nombre o email..."
            value={search} onChange={e => setSearch(e.target.value)} />

          {/* LISTA */}
          <div>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:10,fontSize:'.82rem',color:'#64748b',fontWeight:600}}>
              <span>{filtered.length} reserva{filtered.length!==1?'s':''} · {stats.total} persona{stats.total!==1?'s':''}</span>
              <span style={{color:'#10b981'}}>🟢 = Asistió &nbsp; 🟡 = Pendiente</span>
            </div>

            {filtered.map((b: any) => {
              const done = b.checkedIn || b.checkedIn === 1;
              return (
                <div key={b.id} className={`ci-row ${done?'done':''}`}>
                  <div className={`ci-avatar ${done?'done':''}`}>
                    {b.client?.firstName?.charAt(0)}{b.client?.lastName?.charAt(0)}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <p style={{fontWeight:700,fontSize:'.88rem',color:'#1e293b',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                        {b.client?.firstName} {b.client?.lastName}
                      </p>
                      {b.numPersonas > 1 && (
                        <span style={{background:'#dbeafe',color:'#1d4ed8',fontSize:'.65rem',fontWeight:700,padding:'2px 7px',borderRadius:100,flexShrink:0}}>
                          👥 {b.numPersonas} personas
                        </span>
                      )}
                    </div>
                    <p style={{fontSize:'.72rem',color:'#64748b',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{b.client?.email}</p>
                    {done && b.checkedInAt && (
                      <p style={{fontSize:'.68rem',color:'#10b981',marginTop:2}}>
                        ✅ {new Date(b.checkedInAt).toLocaleTimeString('es-ES',{hour:'2-digit',minute:'2-digit'})}
                      </p>
                    )}
                  </div>
                  {!done ? (
                    <button className="ci-btn" onClick={() => doCheckin(b.id, b.client, false, b.numPersonas || 1)}
                      style={{background:'#2563eb',color:'white',boxShadow:'0 2px 8px rgba(37,99,235,.3)'}}>
                      ✅ Check-in
                    </button>
                  ) : (
                    <span style={{background:'#dcfce7',color:'#15803d',fontSize:'.75rem',fontWeight:700,padding:'6px 12px',borderRadius:8}}>
                      ✓ Asistió
                    </span>
                  )}
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>
                <div style={{fontSize:'2rem',marginBottom:10}}>👥</div>
                <p>{search ? 'No encontrado' : 'Sin inscritos en este evento'}</p>
              </div>
            )}
          </div>
        </>
      )}

      {!selectedEvent && (
        <div className="card p-12 text-center">
          <div style={{fontSize:'3rem',marginBottom:12}}>📅</div>
          <p className="text-secondary-500 font-medium">Selecciona un evento para hacer check-in</p>
        </div>
      )}

      {/* MODAL CONFIRMAR CHECK-IN */}
      {checkinModal && (
        <div className="modal-bg" onClick={() => setCheckinModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{textAlign:'center',marginBottom:20}}>
              <div style={{width:56,height:56,background:'linear-gradient(135deg,#2563eb,#7c3aed)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.5rem',margin:'0 auto 12px'}}>✅</div>
              <h3 style={{fontWeight:800,fontSize:'1.05rem',color:'#1e293b',marginBottom:4}}>Confirmar check-in</h3>
              <p style={{fontSize:'.85rem',color:'#475569'}}><strong>{checkinModal.client?.firstName} {checkinModal.client?.lastName}</strong></p>
              <p style={{fontSize:'.75rem',color:'#94a3b8',marginBottom:4}}>Reservó para {checkinModal.numPersonas} persona{checkinModal.numPersonas!==1?'s':''}</p>
            </div>

            <div style={{marginBottom:20}}>
              <p style={{fontSize:'.82rem',fontWeight:700,color:'#475569',marginBottom:10,textAlign:'center'}}>¿Cuántas personas han llegado?</p>
              <div style={{display:'flex',alignItems:'center',border:'1.5px solid #e2e8f0',borderRadius:12,overflow:'hidden'}}>
                <button onClick={() => setPersonasLlegaron(Math.max(1,personasLlegaron-1))}
                  style={{width:48,height:48,border:'none',background:'#f8fafc',cursor:'pointer',fontSize:'1.3rem',fontWeight:700,color:'#1e293b'}}>−</button>
                <div style={{flex:1,textAlign:'center',fontSize:'1.3rem',fontWeight:800,color:'#1e293b'}}>{personasLlegaron}</div>
                <button onClick={() => setPersonasLlegaron(Math.min(checkinModal.numPersonas,personasLlegaron+1))}
                  style={{width:48,height:48,border:'none',background:'#f8fafc',cursor:'pointer',fontSize:'1.3rem',fontWeight:700,color:'#1e293b'}}>+</button>
              </div>
              {personasLlegaron < checkinModal.numPersonas && (
                <p style={{fontSize:'.75rem',color:'#f59e0b',marginTop:8,textAlign:'center',fontWeight:600}}>
                  ⚠️ {checkinModal.numPersonas - personasLlegaron} persona{checkinModal.numPersonas-personasLlegaron!==1?'s':''} no han llegado
                </p>
              )}
            </div>

            <div style={{display:'flex',gap:10}}>
              <button onClick={() => setCheckinModal(null)}
                style={{flex:1,padding:'12px',borderRadius:11,border:'1.5px solid #e2e8f0',background:'white',fontWeight:600,fontSize:'.85rem',cursor:'pointer',fontFamily:'inherit',color:'#475569'}}>
                Cancelar
              </button>
              <button onClick={() => confirmarCheckin(personasLlegaron)}
                style={{flex:2,padding:'12px',borderRadius:11,border:'none',background:'#2563eb',color:'white',fontWeight:700,fontSize:'.9rem',cursor:'pointer',fontFamily:'inherit',boxShadow:'0 4px 12px rgba(37,99,235,.3)'}}>
                ✅ Confirmar ({personasLlegaron} persona{personasLlegaron!==1?'s':''})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL RESULTADO */}
      {checkinResult && (
        <div className="modal-bg" onClick={() => { setCheckinResult(null); loadCheckins(); }}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            {checkinResult.already ? (
              <>
                <div style={{width:72,height:72,background:'#fef9c3',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'2.2rem',margin:'0 auto 16px'}}>⚠️</div>
                <h3 style={{fontWeight:800,fontSize:'1.1rem',color:'#1e293b',marginBottom:8}}>Ya hizo check-in</h3>
                <p style={{fontSize:'.9rem',color:'#475569',marginBottom:4}}><strong>{checkinResult.client?.firstName} {checkinResult.client?.lastName}</strong></p>
                <p style={{fontSize:'.82rem',color:'#94a3b8',marginBottom:24}}>Este cliente ya registró su entrada</p>
                <button onClick={() => { setCheckinResult(null); loadCheckins(); }} style={{width:'100%',padding:'13px',borderRadius:12,border:'none',background:'#f59e0b',color:'white',fontWeight:700,fontSize:'.92rem',cursor:'pointer',fontFamily:'inherit'}}>
                  Entendido
                </button>
              </>
            ) : (
              <>
                <div style={{width:72,height:72,background:'#dcfce7',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'2.2rem',margin:'0 auto 16px'}}>🎉</div>
                <h3 style={{fontWeight:800,fontSize:'1.1rem',color:'#15803d',marginBottom:8}}>¡Check-in realizado!</h3>
                <p style={{fontSize:'.9rem',color:'#1e293b',marginBottom:4}}><strong>{checkinResult.client?.firstName} {checkinResult.client?.lastName}</strong></p>
                <p style={{fontSize:'.82rem',color:'#64748b',marginBottom:8}}>{checkinResult.client?.email}</p>
                {checkinResult.personasLlegaron && (
                  <div style={{background:'#f0fdf4',borderRadius:10,padding:'10px 16px',marginBottom:16,fontSize:'.85rem',color:'#15803d',fontWeight:600}}>
                    👥 {checkinResult.personasLlegaron} de {checkinResult.totalReservadas} persona{checkinResult.totalReservadas!==1?'s':''} han llegado
                  </div>
                )}
                <button onClick={() => { setCheckinResult(null); loadCheckins(); }} style={{width:'100%',padding:'13px',borderRadius:12,border:'none',background:'#10b981',color:'white',fontWeight:700,fontSize:'.92rem',cursor:'pointer',fontFamily:'inherit'}}>
                  ✓ Continuar
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
